import React, { useState, useEffect } from 'react';
import { authFetch } from '../lib/authFetch';
import { Search, Filter, Calendar, Phone, MapPin, Clock, User, RefreshCw, CheckCircle2, XCircle, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const AdminDashboard = () => {
  const [bookings, setBookings] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    scheduled: 0,
    completed: 0,
    cancelled: 0
  });

  const fetchBookings = async () => {
    setLoading(true);
    try {
      const response = await authFetch(`/api/bookings`);
      if (!response.ok) throw new Error('Failed to fetch bookings');
      
      const data = await response.json();
      setBookings(data);
      calculateStats(data);
      filterBookings(data, searchTerm, statusFilter);
    } catch (error) {
      toast.error('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (data) => {
    setStats({
      total: data.length,
      pending: data.filter(b => b.status === 'Pending').length,
      scheduled: data.filter(b => b.status === 'Scheduled').length,
      completed: data.filter(b => b.status === 'Completed').length,
      cancelled: data.filter(b => b.status === 'Cancelled').length,
    });
  };

  const filterBookings = (data, search, status) => {
    let filtered = data;

    if (status !== 'all') {
      filtered = filtered.filter(b => b.status === status);
    }

    if (search) {
      filtered = filtered.filter(b =>
        b.patient_name.toLowerCase().includes(search.toLowerCase()) ||
        b.contact_phone.includes(search) ||
        b.pickup_address.toLowerCase().includes(search.toLowerCase()) ||
        b.dropoff_address.toLowerCase().includes(search.toLowerCase())
      );
    }

    setFilteredBookings(filtered);
  };

  const updateBookingStatus = async (bookingId, newStatus) => {
    try {
      const response = await authFetch(`/api/bookings/${bookingId}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status: newStatus }),
      });

      if (!response.ok) throw new Error('Failed to update status');

      toast.success(`Booking status updated to ${newStatus}`);
      fetchBookings(); // Refresh list
    } catch (error) {
      toast.error('Failed to update booking status');
    }
  };

  useEffect(() => {
    fetchBookings();
    // Mount-only fetch. `fetchBookings` is intentionally excluded from deps —
    // it's recreated on every render (not memoized), so including it caused
    // an infinite refetch loop that hammered /api/bookings AND blocked
    // Playwright's networkidle during prerender.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    filterBookings(bookings, searchTerm, statusFilter);
  }, [searchTerm, statusFilter, bookings]);

  const getStatusColor = (status) => {
    const colors = {
      Pending: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      Scheduled: 'bg-blue-100 text-blue-800 border-blue-300',
      Assigned: 'bg-purple-100 text-purple-800 border-purple-300',
      Completed: 'bg-green-100 text-green-800 border-green-300',
      Cancelled: 'bg-red-100 text-red-800 border-red-300',
    };
    return colors[status] || 'bg-gray-100 text-gray-800 border-gray-300';
  };

  const getStatusIcon = (status) => {
    const icons = {
      Pending: AlertCircle,
      Scheduled: Calendar,
      Assigned: User,
      Completed: CheckCircle2,
      Cancelled: XCircle,
    };
    const Icon = icons[status] || AlertCircle;
    return <Icon className="w-4 h-4" />;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen pt-24 bg-gray-50">
      {/* Header */}
      <section className="bg-[#004C97] py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">Dispatch Admin Panel</h1>
              <p className="text-gray-200">Manage all ride bookings and requests</p>
            </div>
            <Button
              onClick={fetchBookings}
              className="bg-[#F9A825] text-white hover:bg-[#E08E1E]"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <Card>
            <CardContent className="p-4">
              <div className="text-sm text-gray-600">Total Bookings</div>
              <div className="text-2xl font-bold text-[#004C97]">{stats.total}</div>
            </CardContent>
          </Card>
          <Card className="border-2 border-yellow-400">
            <CardContent className="p-4">
              <div className="text-sm text-gray-600">Pending</div>
              <div className="text-2xl font-bold text-yellow-600">{stats.pending}</div>
            </CardContent>
          </Card>
          <Card className="border-2 border-blue-400">
            <CardContent className="p-4">
              <div className="text-sm text-gray-600">Scheduled</div>
              <div className="text-2xl font-bold text-blue-600">{stats.scheduled}</div>
            </CardContent>
          </Card>
          <Card className="border-2 border-green-400">
            <CardContent className="p-4">
              <div className="text-sm text-gray-600">Completed</div>
              <div className="text-2xl font-bold text-green-600">{stats.completed}</div>
            </CardContent>
          </Card>
          <Card className="border-2 border-red-400">
            <CardContent className="p-4">
              <div className="text-sm text-gray-600">Cancelled</div>
              <div className="text-2xl font-bold text-red-600">{stats.cancelled}</div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                  <Input
                    placeholder="Search by name, phone, or address..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              <div className="w-full md:w-48">
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="Pending">Pending</SelectItem>
                    <SelectItem value="Scheduled">Scheduled</SelectItem>
                    <SelectItem value="Assigned">Assigned</SelectItem>
                    <SelectItem value="Completed">Completed</SelectItem>
                    <SelectItem value="Cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Bookings List */}
        {loading ? (
          <Card>
            <CardContent className="p-12 text-center">
              <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-[#F9A825]" />
              <p className="text-gray-600">Loading bookings...</p>
            </CardContent>
          </Card>
        ) : filteredBookings.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <p className="text-gray-600 text-lg">No bookings found</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredBookings.map((booking) => (
              <Card key={booking.id} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                    {/* Patient Info */}
                    <div className="lg:col-span-2">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="text-lg font-bold text-[#004C97] mb-1">{booking.patient_name}</h3>
                          {booking.facility_name && (
                            <p className="text-sm text-gray-600">Facility: {booking.facility_name}</p>
                          )}
                        </div>
                        <Badge className={`${getStatusColor(booking.status)} border flex items-center gap-1`}>
                          {getStatusIcon(booking.status)}
                          {booking.status}
                        </Badge>
                      </div>

                      <div className="space-y-2">
                        <div className="flex items-start space-x-2 text-sm">
                          <MapPin className="w-4 h-4 text-[#F9A825] mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-gray-700"><strong>Pickup:</strong> {booking.pickup_address}</p>
                            <p className="text-gray-700"><strong>Drop-off:</strong> {booking.dropoff_address}</p>
                          </div>
                        </div>

                        <div className="flex items-center space-x-4 text-sm">
                          <div className="flex items-center space-x-1">
                            <Calendar className="w-4 h-4 text-[#F9A825]" />
                            <span className="text-gray-700">{booking.date}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <Clock className="w-4 h-4 text-[#F9A825]" />
                            <span className="text-gray-700">{booking.time}</span>
                          </div>
                        </div>

                        <div className="flex items-center space-x-2 text-sm">
                          <Phone className="w-4 h-4 text-[#F9A825]" />
                          <a href={`tel:${booking.contact_phone}`} className="text-[#004C97] hover:underline">
                            {booking.contact_phone}
                          </a>
                        </div>
                      </div>
                    </div>

                    {/* Booking Details */}
                    <div className="space-y-3">
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Mobility Type</p>
                        <p className="text-sm font-medium capitalize">{booking.mobility_type}</p>
                      </div>
                      {booking.return_trip && (
                        <div>
                          <p className="text-xs text-gray-500 mb-1">Return Trip</p>
                          <p className="text-sm font-medium capitalize">{booking.return_trip}</p>
                        </div>
                      )}
                      {booking.special_instructions && (
                        <div>
                          <p className="text-xs text-gray-500 mb-1">Special Instructions</p>
                          <p className="text-sm text-gray-700">{booking.special_instructions}</p>
                        </div>
                      )}
                      <div>
                        <p className="text-xs text-gray-500">Submitted: {formatDate(booking.created_at)}</p>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="space-y-2">
                      <p className="text-xs font-semibold text-gray-700 mb-2">Update Status:</p>
                      <div className="space-y-2">
                        <Button
                          size="sm"
                          onClick={() => updateBookingStatus(booking.id, 'Scheduled')}
                          disabled={booking.status === 'Scheduled'}
                          className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                        >
                          Schedule
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => updateBookingStatus(booking.id, 'Assigned')}
                          disabled={booking.status === 'Assigned'}
                          className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                        >
                          Assign
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => updateBookingStatus(booking.id, 'Completed')}
                          disabled={booking.status === 'Completed'}
                          className="w-full bg-green-600 hover:bg-green-700 text-white"
                        >
                          Complete
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => updateBookingStatus(booking.id, 'Cancelled')}
                          disabled={booking.status === 'Cancelled'}
                          variant="destructive"
                          className="w-full"
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;